import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonGroup } from 'foundations-components/transitional/components/ButtonGroup';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { Link } from 'foundations-components/transitional/components/Link';
import { Media } from 'foundations-components/transitional/components/Media';
import { MediaBody } from 'foundations-components/transitional/components/MediaBody';
import { MediaLeft } from 'foundations-components/transitional/components/MediaLeft';
import { MediaRight } from 'foundations-components/transitional/components/MediaRight';
import { Small } from 'foundations-components/transitional/components/Small';
import { StatusDot } from 'foundations-components/transitional/components/StatusDot';
import { Tag } from 'foundations-components/transitional/components/Tag';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ACTIONS, SCREENS } from '../../constants/Tracking';
import { AppContext } from '../../contexts/app/context';
import { ContactsPropsContext } from '../../contexts/contacts/PropsContext';
import EditHOC from '../../hoc/EditHOC';
import { ContactsAvatarWrapper, ContactsNameWrapper } from '../../styles/contacts';
import Avatar from '../common/Avatar';
import Notification from '../common/Notification/Notification';
import { DeleteIcon } from 'foundations-assets/icons/components/DeleteIcon';
import { EditIcon } from 'foundations-assets/icons/components/EditIcon';
import { MergeIcon } from 'foundations-assets/icons/components/MergeIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const RIGHT_SECTION_LEFT_MARGIN = 16;
function ContactListRow({
  email,
  participantName,
  onClickAssociate,
  openPopupToUnmap,
  isHost = false,
  contactId,
  showProfileLink,
  participantId,
  openPreviewModal,
  isAutoMapped,
  windowWidth
}) {
  const {
    usageTracker,
    methods,
    participants,
    notifications,
    myParticipantRole,
    openUrl,
    featureTipsLink,
    apiWrapper,
    portalId
  } = useContext(ContactsPropsContext);
  const {
    isPastMeeting,
    appName
  } = useContext(AppContext);
  const containerRef = useRef(null);
  const leftElementRef = useRef(null);
  const rightElementRef = useRef(null);
  const [nameWidth, setNameWidth] = useState(0);
  const [leftWidth, setLeftWidth] = useState(0);
  const [rightWidth, setRightWidth] = useState(0);
  const avatar = /*#__PURE__*/_jsx(ContactsAvatarWrapper, {
    ref: leftElementRef,
    children: /*#__PURE__*/_jsx(Notification, {
      participantId: participantId,
      pointer: true,
      usageTracker: usageTracker,
      appName: appName,
      markNotificationAsRead: methods.markNotificationAsRead,
      participants: participants,
      notifications: notifications,
      myParticipantRole: myParticipantRole,
      children: /*#__PURE__*/_jsx(Avatar, Object.assign({}, Object.assign({}, contactId && email && {
        email,
        vid: Number(contactId)
      }, !(contactId && email) && {
        type: 'contact'
      }, {
        size: 'responsive',
        getAvatarsApi: apiWrapper.getAvatars,
        portalId
      })))
    })
  });
  const onContactNameClick = useCallback(() => {
    openPreviewModal(contactId);
  }, [contactId, openPreviewModal]);
  const NameWrapper = useCallback(({
    children
  }) => {
    if (contactId) {
      return /*#__PURE__*/_jsx(ContactsNameWrapper, {
        children: /*#__PURE__*/_jsx(Link, {
          "data-test-id": `${appName}-participants-contact-name-button`,
          onClick: onContactNameClick,
          children: children
        })
      });
    }
    return /*#__PURE__*/_jsx(ContactsNameWrapper, {
      children: children
    });
  }, [contactId, onContactNameClick, appName]);
  const knowlegeBaseClickHandler = event => {
    event.stopPropagation();
    usageTracker.trackInteraction(Object.assign({
      screen: SCREENS.participants
    }, ACTIONS.participants.openKnowledgeBase));
    openUrl(featureTipsLink);
  };
  const tooltipClickHandler = event => {
    event.stopPropagation();
  };

  // get left section width
  useEffect(() => {
    if (leftElementRef.current) {
      setLeftWidth(leftElementRef.current.offsetWidth);
    }
  }, []);

  // get right section width
  useEffect(() => {
    if (rightElementRef.current) {
      setRightWidth(rightElementRef.current.offsetWidth);
    }
  }, []);

  // calculate name section width
  useEffect(() => {
    if (containerRef.current && isAutoMapped && contactId) {
      setNameWidth(containerRef.current.offsetWidth - leftWidth - rightWidth - RIGHT_SECTION_LEFT_MARGIN);
    }
  }, [contactId, isAutoMapped, leftWidth, rightWidth, windowWidth]);
  return /*#__PURE__*/_jsx("div", {
    ref: containerRef,
    children: /*#__PURE__*/_jsxs(Media, {
      align: "center",
      spacing: "medium",
      "data-test-id": `${appName}-participants-item`,
      paddingBlock: "200",
      children: [/*#__PURE__*/_jsx(MediaLeft, {
        children: avatar
      }), /*#__PURE__*/_jsx(MediaBody, {
        children: /*#__PURE__*/_jsx(Button, {
          use: "unstyled",
          disabled: !contactId || !showProfileLink,
          padding: "0",
          margin: "0",
          block: true,
          children: /*#__PURE__*/_jsxs(Flex, {
            flexDirection: "column",
            alignItems: "start",
            flexGrow: 1,
            children: [participantName && /*#__PURE__*/_jsx(NameWrapper, {
              children: /*#__PURE__*/_jsx(TruncateString, {
                matchContentWidth: true,
                maxWidth: nameWidth || undefined,
                fixedChildren: isAutoMapped && contactId ? /*#__PURE__*/_jsx("span", {
                  onClick: tooltipClickHandler,
                  children: /*#__PURE__*/_jsx(Tooltip, {
                    title: /*#__PURE__*/_jsx(FormattedReactMessage, {
                      message: "contactList.tooltip.autoMapTooltip",
                      options: {
                        link: /*#__PURE__*/_jsx(Link, {
                          use: "on-dark",
                          external: true,
                          onClick: knowlegeBaseClickHandler,
                          children: /*#__PURE__*/_jsx(FormattedMessage, {
                            message: "contactList.tooltip.autoMapTooltipLink"
                          })
                        })
                      }
                    }),
                    children: /*#__PURE__*/_jsx(View, {
                      as: "span",
                      marginInlineStart: "200",
                      position: "relative",
                      insetBlockStart: "100",
                      children: /*#__PURE__*/_jsx(MergeIcon, {
                        color: "icon-tertiary-default",
                        size: "300"
                      })
                    })
                  })
                }) : null,
                children: participantName
              })
            }), /*#__PURE__*/_jsx(Small, {
              use: "help",
              children: /*#__PURE__*/_jsxs(TruncateString, {
                matchContentWidth: true,
                children: [/*#__PURE__*/_jsx(StatusDot, {
                  use: contactId ? 'success' : 'error'
                }), contactId ? email : /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "contactList.status.notMapped"
                })]
              })
            })]
          })
        })
      }), /*#__PURE__*/_jsx(MediaRight, {
        minInlineSize: "unset",
        children: /*#__PURE__*/_jsxs("div", {
          ref: rightElementRef,
          children: [isHost && /*#__PURE__*/_jsx(Tag, {
            use: "success",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "contactList.status.host"
            })
          }), !contactId ? /*#__PURE__*/_jsx(EditHOC, {
            isPastMeeting: isPastMeeting,
            children: /*#__PURE__*/_jsx(Button, {
              "data-test-id": `${appName}-participants-mapping-button`,
              use: "tertiary-light",
              onClick: onClickAssociate,
              responsive: false,
              size: "xs",
              borderRadius: "200",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: `contactList.map`
              })
            })
          }) : /*#__PURE__*/_jsx(EditHOC, {
            isPastMeeting: isPastMeeting,
            children: /*#__PURE__*/_jsx(Text, {
              as: "span",
              whiteSpace: "nowrap",
              children: /*#__PURE__*/_jsxs(ButtonGroup, {
                children: [/*#__PURE__*/_jsx(IconButton, {
                  onClick: onClickAssociate,
                  tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: `contactList.edit`
                  }),
                  size: "xs",
                  borderRadius: "200",
                  children: /*#__PURE__*/_jsx(EditIcon, {})
                }), /*#__PURE__*/_jsx(IconButton, {
                  "data-test-id": `${appName}-participants-unmapping-button`,
                  use: "tertiary-light",
                  borderRadius: "200",
                  onClick: openPopupToUnmap,
                  tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: `contactList.remove`
                  }),
                  size: "xs",
                  children: /*#__PURE__*/_jsx(DeleteIcon, {})
                })]
              })
            })
          })]
        })
      })]
    })
  });
}
export default ContactListRow;